import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import {
  ArrowLeftIcon, PlusIcon, TrashIcon, LockClosedIcon, CheckCircleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {
  getTreatmentPlan, createTreatmentPlan, updateTreatmentPlan, signTreatmentPlan,
} from '../services/treatmentPlanService';
import { getClient } from '../services/clientService';
import Spinner from '../components/common/Spinner';
import Badge from '../components/common/Badge';

const STATUS_VARIANTS = { draft: 'default', active: 'info', completed: 'success', discontinued: 'inactive' };

const EMPTY_GOAL = () => ({
  id: crypto.randomUUID(),
  description: '',
  objective: '',
  targetDate: '',
  status: 'active',
  interventions: [''],
});

const EMPTY_DIAGNOSIS = () => ({ code: '', description: '' });

function GoalCard({ goal, index, onChange, onRemove, locked }) {
  const update = (field, value) => onChange({ ...goal, [field]: value });

  const updateIntervention = (i, value) => {
    const interventions = [...goal.interventions];
    interventions[i] = value;
    update('interventions', interventions);
  };

  const addIntervention = () => update('interventions', [...goal.interventions, '']);
  const removeIntervention = (i) => update('interventions', goal.interventions.filter((_, idx) => idx !== i));

  return (
    <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mt-1">
          Goal {index + 1}
        </span>
        <div className="flex items-center gap-2">
          <select
            value={goal.status}
            onChange={(e) => update('status', e.target.value)}
            disabled={locked}
            className="text-xs border border-neutral-200 dark:border-neutral-700 rounded px-2 py-1 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 disabled:opacity-60"
          >
            <option value="active">Active</option>
            <option value="met">Met</option>
            <option value="discontinued">Discontinued</option>
          </select>
          {!locked && (
            <button onClick={onRemove} className="text-neutral-400 hover:text-red-500 transition-colors">
              <TrashIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
          Goal description <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={goal.description}
          onChange={(e) => update('description', e.target.value)}
          disabled={locked}
          placeholder="e.g. Reduce symptoms of depression and improve daily functioning"
          className="input w-full text-sm disabled:opacity-60"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
          Measurable objective
        </label>
        <input
          type="text"
          value={goal.objective}
          onChange={(e) => update('objective', e.target.value)}
          disabled={locked}
          placeholder="e.g. PHQ-9 score < 5 within 12 weeks"
          className="input w-full text-sm disabled:opacity-60"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
          Target date
        </label>
        <input
          type="date"
          value={goal.targetDate}
          onChange={(e) => update('targetDate', e.target.value)}
          disabled={locked}
          className="input text-sm disabled:opacity-60"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">
          Interventions
        </label>
        <div className="space-y-2">
          {goal.interventions.map((intv, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="text"
                value={intv}
                onChange={(e) => updateIntervention(i, e.target.value)}
                disabled={locked}
                placeholder="e.g. Cognitive behavioral therapy, weekly sessions"
                className="input flex-1 text-sm disabled:opacity-60"
              />
              {!locked && goal.interventions.length > 1 && (
                <button onClick={() => removeIntervention(i)} className="text-neutral-400 hover:text-red-500 transition-colors shrink-0">
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          {!locked && (
            <button
              onClick={addIntervention}
              className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1"
            >
              <PlusIcon className="h-3.5 w-3.5" /> Add intervention
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TreatmentPlanEditor() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = !id;
  const clientIdParam = searchParams.get('clientId');

  // Form state
  const [title, setTitle] = useState('Treatment Plan');
  const [status, setStatus] = useState('draft');
  const [startDate, setStartDate] = useState('');
  const [reviewDate, setReviewDate] = useState('');
  const [presentingProblems, setPresentingProblems] = useState('');
  const [diagnoses, setDiagnoses] = useState([EMPTY_DIAGNOSIS()]);
  const [goals, setGoals] = useState([EMPTY_GOAL()]);
  const [strengths, setStrengths] = useState('');
  const [barriers, setBarriers] = useState('');
  const [dirty, setDirty] = useState(false);

  const clientId = clientIdParam || (!isNew ? undefined : undefined);

  const { data: plan, isLoading: planLoading } = useQuery({
    queryKey: ['treatmentPlan', id],
    queryFn: () => getTreatmentPlan(id),
    enabled: !isNew,
  });

  const { data: clientData } = useQuery({
    queryKey: ['client', plan?.clientId || clientId],
    queryFn: () => getClient(plan?.clientId || clientId),
    enabled: !!(plan?.clientId || clientId),
  });

  const client = clientData?.client;
  const locked = plan?.isLocked ?? false;

  // Populate form when plan loads
  useEffect(() => {
    if (!plan) return;
    setTitle(plan.title || 'Treatment Plan');
    setStatus(plan.status || 'draft');
    setStartDate(plan.startDate || '');
    setReviewDate(plan.reviewDate || '');
    setPresentingProblems(plan.presentingProblems || '');
    setDiagnoses(plan.diagnoses?.length ? plan.diagnoses : [EMPTY_DIAGNOSIS()]);
    setGoals(plan.goals?.length ? plan.goals : [EMPTY_GOAL()]);
    setStrengths(plan.strengths || '');
    setBarriers(plan.barriers || '');
    setDirty(false);
  }, [plan]);

  const buildPayload = () => ({
    clientId: plan?.clientId || clientId,
    title,
    status,
    startDate: startDate || null,
    reviewDate: reviewDate || null,
    presentingProblems: presentingProblems || null,
    diagnoses: diagnoses.filter((d) => d.code || d.description),
    goals: goals.filter((g) => g.description),
    strengths: strengths || null,
    barriers: barriers || null,
  });

  const createMutation = useMutation({
    mutationFn: createTreatmentPlan,
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['treatmentPlans', saved.clientId] });
      toast.success('Treatment plan created');
      navigate(`/treatment-plans/${saved.id}`);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to create'),
  });

  const updateMutation = useMutation({
    mutationFn: (data) => updateTreatmentPlan(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['treatmentPlan', id] });
      queryClient.invalidateQueries({ queryKey: ['treatmentPlans', plan?.clientId] });
      setDirty(false);
      toast.success('Saved');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to save'),
  });

  const signMutation = useMutation({
    mutationFn: () => signTreatmentPlan(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['treatmentPlan', id] });
      queryClient.invalidateQueries({ queryKey: ['treatmentPlans', plan?.clientId] });
      toast.success('Treatment plan signed and locked');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to sign'),
  });

  const handleSave = () => {
    if (isNew) {
      createMutation.mutate(buildPayload());
    } else {
      updateMutation.mutate(buildPayload());
    }
  };

  const handleSign = () => {
    if (!window.confirm('Sign and lock this treatment plan? It cannot be edited after signing.')) return;
    signMutation.mutate();
  };

  const markDirty = (setter) => (val) => { setter(val); setDirty(true); };

  const addGoal = () => { setGoals((g) => [...g, EMPTY_GOAL()]); setDirty(true); };
  const updateGoal = (i, updated) => { setGoals((g) => g.map((x, idx) => idx === i ? updated : x)); setDirty(true); };
  const removeGoal = (i) => { setGoals((g) => g.filter((_, idx) => idx !== i)); setDirty(true); };

  const addDiagnosis = () => { setDiagnoses((d) => [...d, EMPTY_DIAGNOSIS()]); setDirty(true); };
  const updateDiagnosis = (i, field, val) => { setDiagnoses((d) => d.map((x, idx) => idx === i ? { ...x, [field]: val } : x)); setDirty(true); };
  const removeDiagnosis = (i) => { setDiagnoses((d) => d.filter((_, idx) => idx !== i)); setDirty(true); };

  if (!isNew && planLoading) {
    return <div className="flex justify-center py-20"><Spinner /></div>;
  }

  const backHref = plan?.clientId
    ? `/clients/${plan.clientId}?tab=treatment_plans`
    : clientId
      ? `/clients/${clientId}?tab=treatment_plans`
      : '/clients';

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="max-w-3xl mx-auto space-y-6 pb-12">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Link to={backHref} className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 mt-1">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
              {isNew ? 'New Treatment Plan' : title}
            </h1>
            {!isNew && <Badge label={status} variant={STATUS_VARIANTS[status] || 'default'} />}
            {locked && (
              <span className="flex items-center gap-1 text-xs font-medium text-neutral-500 dark:text-neutral-400">
                <LockClosedIcon className="h-3.5 w-3.5" /> Signed {plan?.signedAt ? format(parseISO(plan.signedAt), 'MMM d, yyyy') : ''}
              </span>
            )}
          </div>
          {client && (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
              {client.firstName} {client.lastName}
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0">
          {!locked && (
            <button
              onClick={handleSave}
              disabled={isSaving || (!dirty && !isNew)}
              className="btn btn-primary text-sm"
            >
              {isSaving ? <Spinner size="sm" /> : isNew ? 'Create' : 'Save'}
            </button>
          )}
          {!isNew && !locked && (
            <button
              onClick={handleSign}
              disabled={signMutation.isPending || dirty}
              className="btn btn-secondary text-sm flex items-center gap-1.5"
              title={dirty ? 'Save first before signing' : undefined}
            >
              <LockClosedIcon className="h-4 w-4" />
              Sign & Lock
            </button>
          )}
        </div>
      </div>

      {dirty && !locked && (
        <p className="text-xs text-amber-600 dark:text-amber-400">Unsaved changes</p>
      )}

      {/* Plan meta */}
      <div className="card space-y-4">
        <h2 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 uppercase tracking-wide">Plan Details</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => markDirty(setTitle)(e.target.value)}
              disabled={locked}
              className="input w-full disabled:opacity-60"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">Status</label>
            <select
              value={status}
              onChange={(e) => markDirty(setStatus)(e.target.value)}
              disabled={locked}
              className="input w-full disabled:opacity-60"
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="discontinued">Discontinued</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">Start date</label>
            <input type="date" value={startDate} onChange={(e) => markDirty(setStartDate)(e.target.value)} disabled={locked} className="input w-full disabled:opacity-60" />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 dark:text-neutral-400 mb-1">Review date</label>
            <input type="date" value={reviewDate} onChange={(e) => markDirty(setReviewDate)(e.target.value)} disabled={locked} className="input w-full disabled:opacity-60" />
          </div>
        </div>
      </div>

      {/* Presenting problems */}
      <div className="card space-y-3">
        <h2 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 uppercase tracking-wide">Presenting Problems</h2>
        <textarea
          value={presentingProblems}
          onChange={(e) => markDirty(setPresentingProblems)(e.target.value)}
          disabled={locked}
          rows={4}
          placeholder="Describe the client's primary concerns, symptoms, and reason for treatment..."
          className="input w-full resize-y disabled:opacity-60"
        />
      </div>

      {/* Diagnoses */}
      <div className="card space-y-3">
        <h2 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 uppercase tracking-wide">Diagnoses</h2>
        <div className="space-y-2">
          {diagnoses.map((dx, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="text"
                value={dx.code}
                onChange={(e) => updateDiagnosis(i, 'code', e.target.value)}
                disabled={locked}
                placeholder="ICD-10 code"
                className="input w-28 text-sm disabled:opacity-60 shrink-0"
              />
              <input
                type="text"
                value={dx.description}
                onChange={(e) => updateDiagnosis(i, 'description', e.target.value)}
                disabled={locked}
                placeholder="Diagnosis description"
                className="input flex-1 text-sm disabled:opacity-60"
              />
              {!locked && diagnoses.length > 1 && (
                <button onClick={() => removeDiagnosis(i)} className="text-neutral-400 hover:text-red-500 transition-colors shrink-0">
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
        </div>
        {!locked && (
          <button onClick={addDiagnosis} className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1">
            <PlusIcon className="h-3.5 w-3.5" /> Add diagnosis
          </button>
        )}
      </div>

      {/* Goals */}
      <div className="card space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 uppercase tracking-wide">
            Treatment Goals
          </h2>
          {!locked && (
            <button onClick={addGoal} className="btn btn-secondary text-xs flex items-center gap-1">
              <PlusIcon className="h-3.5 w-3.5" /> Add goal
            </button>
          )}
        </div>
        {goals.length === 0 ? (
          <p className="text-sm text-neutral-500 dark:text-neutral-400 text-center py-4">No goals yet.</p>
        ) : (
          <div className="space-y-4">
            {goals.map((goal, i) => (
              <GoalCard
                key={goal.id}
                goal={goal}
                index={i}
                onChange={(updated) => updateGoal(i, updated)}
                onRemove={() => removeGoal(i)}
                locked={locked}
              />
            ))}
          </div>
        )}
      </div>

      {/* Strengths & Barriers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="card space-y-3">
          <h2 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 uppercase tracking-wide">Client Strengths</h2>
          <textarea
            value={strengths}
            onChange={(e) => markDirty(setStrengths)(e.target.value)}
            disabled={locked}
            rows={4}
            placeholder="Motivation, support system, coping skills..."
            className="input w-full resize-y disabled:opacity-60"
          />
        </div>
        <div className="card space-y-3">
          <h2 className="text-sm font-semibold text-neutral-700 dark:text-neutral-300 uppercase tracking-wide">Barriers to Treatment</h2>
          <textarea
            value={barriers}
            onChange={(e) => markDirty(setBarriers)(e.target.value)}
            disabled={locked}
            rows={4}
            placeholder="Transportation, financial, co-occurring conditions..."
            className="input w-full resize-y disabled:opacity-60"
          />
        </div>
      </div>

      {/* Signature confirmation */}
      {locked && (
        <div className="flex items-start gap-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 p-4 text-sm text-green-800 dark:text-green-300">
          <CheckCircleIcon className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Signed and locked</p>
            <p className="text-green-700 dark:text-green-400 mt-0.5">
              Signed on {plan?.signedAt ? format(parseISO(plan.signedAt), 'MMMM d, yyyy \'at\' h:mm a') : '—'}
              {plan?.practitioner?.user && ` by ${plan.practitioner.user.firstName} ${plan.practitioner.user.lastName}`}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
